import React, { useState } from 'react';
import {
    motion,
    useMotionValueEvent,
    useReducedMotion,
    useTransform,
} from 'framer-motion';
import {
    Archive,
    Bell,
    Captions,
    Clock,
    FileText,
    Hand,
    Info,
    Mail,
    MessageSquare,
    Mic,
    MicOff,
    MoreVertical,
    Phone,
    Search,
    Sparkles,
    Trash2,
    Users,
    Video,
} from 'lucide-react';
import LandingDoraSequence, {
    CATCH_FRAMES,
    FLOW_FRAMES,
    LandingDoraEmbed,
    MEET_FRAMES,
} from '@/components/LandingDoraSequence';
import LandingFace from '@/components/LandingFace';
import LandingCastMark from '@/components/LandingCastMark';
import AccountabilityScore from '@/components/AccountabilityScore';
import LandingPinBeat from '@/components/LandingPinBeat';
import { TskFlowMark } from '@/components/TskFlowLogo';
import { CAST, TASKS } from '@/lib/landingCast';

const MEET_PEOPLE = [
    { who: 'alex', you: true, mute: false, speakAt: [0.18, 0.46], task: null },
    { who: 'maya', you: false, mute: false, speakAt: [0.46, 0.70], task: TASKS[0] },
    { who: 'chris', you: false, mute: true, speakAt: null, task: TASKS[1] },
    { who: 'priya', you: false, mute: false, speakAt: null, task: TASKS[2] },
];

const MEET_CAPTIONS = [
    { at: 0, lock: 'A group of people.', text: 'They are on a call. Work is about to get a name and a date.' },
    { at: 0.25, lock: 'The organizer assigned a task.', text: 'Alex asked Maya for the Q3 forecast by Friday.' },
    { at: 0.5, lock: 'Everyone acknowledged.', text: 'Maya said yes. Chris and Priya said yes too.' },
    { at: 0.75, lock: 'The meeting concluded.', text: 'The meeting ended. The promises walked out the door.' },
];

const CATCH_CAPTIONS = [
    { at: 0, lock: 'Now you inspect what you expected.', text: 'Friday. The forecast is not there. Now you go looking.' },
    { at: 0.2, lock: 'Build the report.', text: 'You build a list of who missed.' },
    { at: 0.4, lock: 'Catch people.', text: 'You ping them yourself. You are the nag now.' },
    { at: 0.6, lock: 'Have the tough conversation.', text: 'Then the awkward 1:1. This is the third Friday.' },
    { at: 0.8, lock: 'Document the continuous misses.', text: 'If it keeps happening, you start a file for HR.' },
];

const FLOW_CAPTIONS = [
    { at: 0, lock: 'TskFlow joins your meet.', text: 'TskFlow sits on the same call and writes down every yes.' },
    { at: 0.25, lock: 'Leaves with every task.', text: 'It leaves with every task, owner, and date.' },
    { at: 0.5, lock: 'Gets after the assignees.', text: 'It follows up with Maya. Not you.' },
    { at: 0.75, lock: 'Your relationship stays intact.', text: 'You stay the manager. Not the reminder.' },
];

const LINES = [
    { at: 0.22, who: 'alex', text: 'Maya, send the Q3 forecast by Friday.' },
    { at: 0.50, who: 'maya', text: "Yep, I'll have this done by Friday." },
];

const FILE_STEPS = [
    'Record the 1:1',
    'Screenshot Slack',
    'Write it up yourself',
    'Hope HR can use it',
];

const PERF = [
    { who: 'priya', score: 94, label: 'Strong', done: 12, assigned: 12 },
    { who: 'chris', score: 71, label: 'Solid', done: 9, assigned: 12 },
    { who: 'maya', score: 41, label: 'At risk', done: 4, assigned: 10 },
    { who: 'jordan', score: 18, label: 'Needs work', done: 2, assigned: 11 },
];

const REPORT_ROWS = [
    { who: 'maya', item: 'Q3 forecast', state: 'Missing' },
    { who: 'chris', item: 'Acme proposal', state: 'Late' },
    { who: 'priya', item: 'Salesforce stage', state: 'Done' },
    { who: 'jordan', item: 'Discovery log', state: 'Missing' },
];

const FLOW_STEPS = ['Capture', 'Schedule', 'Follow up', 'Verify'];

const CHASE_LINES = [
    'Did you get this?',
    'Hey - quick update on this?',
    "What's the status?",
];

function lineFor(v) {
    let line = LINES[0];
    for (const item of LINES) {
        if (v >= item.at) line = item;
    }
    return line;
}

function captionAt(captions, v) {
    let beat = captions[0];
    for (const item of captions) {
        if (v >= item.at) beat = item;
    }
    return beat;
}

/** Overlay line on the still. Updates as pin progress crosses each `at`. */
function ScrubCaption({ progress, captions, testId }) {
    const reduce = useReducedMotion();
    const [beat, setBeat] = useState(() => captionAt(captions, progress.get()));
    useMotionValueEvent(progress, 'change', (v) => {
        const next = captionAt(captions, v);
        setBeat((cur) => (cur.lock === next.lock ? cur : next));
    });

    return (
        <div className="landing-dora-caption" data-testid={testId}>
            <motion.p
                key={beat.lock}
                className="landing-pin-now"
                initial={reduce ? false : { opacity: 0.28, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
            >
                {beat.text}
                {captions.map((row) => (
                    <span className="sr-only" key={row.lock}>{row.lock}</span>
                ))}
            </motion.p>
        </div>
    );
}

function FilmChapter({
    testId,
    doraId,
    captionId,
    actId,
    step,
    label,
    navLabel,
    thesis,
    frames,
    captions,
    spans,
    tone,
    children,
}) {
    return (
        <LandingPinBeat
            testId={testId}
            label={label}
            navLabel={navLabel}
            thesis={thesis}
            step={step}
            totalSteps={3}
            spans={spans}
            tone={tone}
        >
            {(progress) => (
                <>
                    <div className="landing-film-stage landing-dora-stage">
                        <div className="landing-dora-card">
                            <LandingDoraSequence progress={progress} frames={frames} testId={doraId} />
                            <ScrubCaption progress={progress} captions={captions} testId={captionId} />
                        </div>
                    </div>
                    <div className="sr-only" data-testid={actId}>
                        {children(progress)}
                    </div>
                </>
            )}
        </LandingPinBeat>
    );
}

export default function LandingFilm() {
    return (
        <div id="landing-film" data-testid="landing-film">
            <LandingDoraEmbed />
            <FilmChapter
                testId="landing-film-meet"
                doraId="landing-dora-meet"
                captionId="landing-film-caption"
                actId="landing-meet-act"
                label="The meeting"
                navLabel="They said yes"
                thesis="They said yes. Then the meeting ended."
                step={1}
                frames={MEET_FRAMES}
                captions={MEET_CAPTIONS}
                spans={3.4}
                tone="calm"
            >
                {(progress) => <MeetScene progress={progress} />}
            </FilmChapter>
            <FilmChapter
                testId="landing-film-catch"
                doraId="landing-dora-catch"
                captionId="landing-film-caption-catch"
                actId="landing-catch-act"
                label="You chase"
                navLabel="You become the nag"
                thesis="After yes, you become the reminder system."
                step={2}
                frames={CATCH_FRAMES}
                captions={CATCH_CAPTIONS}
                spans={4.2}
                tone="wear"
            >
                {(progress) => <CatchScene progress={progress} />}
            </FilmChapter>
            <FilmChapter
                testId="landing-film-flow"
                doraId="landing-dora-flow"
                captionId="landing-film-caption-flow"
                actId="landing-flow-act"
                label="TskFlow takes it"
                navLabel="TskFlow follows up"
                thesis="TskFlow does the reminding so you do not."
                step={3}
                frames={FLOW_FRAMES}
                captions={FLOW_CAPTIONS}
                spans={3.4}
                tone="calm"
            >
                {(progress) => <FlowScene progress={progress} />}
            </FilmChapter>
        </div>
    );
}

function MeetScene({ progress }) {
    const askOp = useTransform(progress, [0.18, 0.28], [0, 1]);
    const endedOp = useTransform(progress, [0.80, 0.90], [0, 1]);
    const leaveScale = useTransform(progress, [0.78, 0.90], [1, 1.18]);
    const gridDim = useTransform(progress, [0.80, 0.90], [1, 0.38]);
    const [line, setLine] = useState(() => lineFor(progress.get()));
    useMotionValueEvent(progress, 'change', (v) => setLine(lineFor(v)));

    return (
        <div className="gmeet gmeet--film" data-testid="landing-meet-frame" aria-label="Google Meet">
            <header className="gmeet-top">
                <span className="gmeet-title">Q3 pipeline</span>
                <span className="gmeet-time">3:11 PM</span>
                <span className="gmeet-top-ico" aria-hidden><Info size={16} /></span>
            </header>
            <motion.div className="gmeet-grid" style={{ opacity: gridDim }}>
                {MEET_PEOPLE.map((tile) => (
                    <MeetTile key={tile.who} tile={tile} progress={progress} />
                ))}
            </motion.div>
            <motion.p className="gmeet-cc" data-testid="landing-meet-caption" style={{ opacity: askOp }}>
                {CAST[line.who].short}: {line.text}
            </motion.p>
            <nav className="gmeet-bar" aria-hidden>
                <span className="gmeet-btn"><Mic size={18} /></span>
                <span className="gmeet-btn"><Video size={18} /></span>
                <span className="gmeet-btn"><Captions size={18} /></span>
                <span className="gmeet-btn"><Hand size={18} /></span>
                <span className="gmeet-btn"><MoreVertical size={18} /></span>
                <motion.span className="gmeet-btn is-leave" style={{ scale: leaveScale }}><Phone size={18} /></motion.span>
            </nav>
            <motion.p className="landing-meet-ended" style={{ opacity: endedOp }} data-testid="landing-meet-ended">
                Meeting ended
            </motion.p>
            <p className="sr-only">Maya Chen owns the Q3 forecast.</p>
        </div>
    );
}

function MeetTile({ tile, progress }) {
    const person = CAST[tile.who];
    const speakFrom = tile.speakAt ? tile.speakAt[0] : 2;
    const speakTo = tile.speakAt ? tile.speakAt[1] : 2.2;
    const speaking = useTransform(
        progress,
        [speakFrom, speakFrom + 0.05, speakTo, speakTo + 0.05],
        tile.speakAt ? [0, 1, 1, 0] : [0, 0, 0, 0],
    );
    const enter = useTransform(progress, [0.02, 0.12], [0.92, 1]);
    const ring = useTransform(speaking, (v) => (v > 0.5 ? 'inset 0 0 0 3px #34a853' : 'inset 0 0 0 0px transparent'));

    return (
        <motion.div
            className={`gmeet-tile${tile.you ? ' is-you' : ''}`}
            data-testid={`landing-meet-tile-${tile.who}`}
            style={{ scale: enter, boxShadow: ring }}
        >
            <LandingFace who={tile.who} size={220} radius={0} />
            <span className="gmeet-name">
                {tile.mute ? <MicOff size={12} /> : <Mic size={12} />}
                {person.short}
                {tile.you ? ' (You)' : ''}
            </span>
            {tile.task ? <TaskChip task={tile.task} progress={progress} at={0.28} /> : null}
        </motion.div>
    );
}

function TaskChip({ task, progress, at }) {
    const opacity = useTransform(progress, [at, at + 0.08], [0, 1]);
    const y = useTransform(progress, [at, at + 0.10], [12, 0]);
    return (
        <motion.span className="landing-meet-task" style={{ opacity, y }} data-testid={`landing-meet-task-${task.id}`}>
            {task.title}
        </motion.span>
    );
}

function CatchScene({ progress }) {
    const inspectOp = useTransform(progress, [0, 0.02, 0.18, 0.26], [1, 1, 1, 0]);
    const reportOp = useTransform(progress, [0.20, 0.28, 0.40, 0.48], [0, 1, 1, 0]);
    const catchOp = useTransform(progress, [0.42, 0.50, 0.60, 0.68], [0, 1, 1, 0]);
    const talkOp = useTransform(progress, [0.62, 0.70, 0.80, 0.88], [0, 1, 1, 0]);
    const fileOp = useTransform(progress, [0.82, 0.90, 1], [0, 1, 1]);

    return (
        <div className="landing-catch" data-testid="landing-catch-frame">
            <motion.div className="landing-film-layer" style={{ opacity: inspectOp }} data-testid="landing-catch-inspect">
                <InspectCard progress={progress} />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: reportOp }} data-testid="landing-catch-report">
                <ReportCard progress={progress} />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: catchOp }} data-testid="landing-catch-people">
                <PeopleCard />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: talkOp }} data-testid="landing-catch-talk">
                <TalkCard />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: fileOp }} data-testid="landing-catch-file">
                <FileCard progress={progress} />
            </motion.div>
        </div>
    );
}

function InspectCard({ progress }) {
    return (
        <article className="landing-inspect">
            <p className="landing-inspect-lead" data-testid="landing-compare-without-lead">
                There is no clear way to hold people accountable.
            </p>
            <div className="landing-inspect-search">
                <Search size={16} />
                <span>Q3 forecast — expected Friday</span>
            </div>
            <ul className="landing-chase-list landing-chase-list--film">
                {CHASE_LINES.map((line, i) => (
                    <InspectPing key={line} line={line} index={i} progress={progress} />
                ))}
            </ul>
        </article>
    );
}

function InspectPing({ line, index, progress }) {
    const at = 0.03 + index * 0.05;
    const opacity = useTransform(progress, [at, at + 0.06], [0, 1]);
    const x = useTransform(progress, [at, at + 0.07], [-16, 0]);
    return (
        <motion.li style={{ opacity, x }}>
            <LandingFace who="alex" size={28} radius={8} />
            {line}
        </motion.li>
    );
}

function ReportCard({ progress }) {
    return (
        <article className="landing-report-card" data-testid="landing-report-card">
            <header>
                <FileText size={16} />
                <span>Weekly miss report</span>
            </header>
            <ul>
                {REPORT_ROWS.map((row, i) => (
                    <ReportRow key={row.who} row={row} index={i} progress={progress} />
                ))}
            </ul>
        </article>
    );
}

function ReportRow({ row, index, progress }) {
    const at = 0.24 + index * 0.04;
    const opacity = useTransform(progress, [at, at + 0.05], [0, 1]);
    const x = useTransform(progress, [at, at + 0.06], [18, 0]);
    const person = CAST[row.who];
    return (
        <motion.li style={{ opacity, x }} data-testid={`landing-report-${row.who}`}>
            <LandingCastMark who={row.who} size="sm" />
            <span>{person.short}</span>
            <b>{row.item}</b>
            <em className={row.state === 'Done' ? 'is-ok' : 'is-miss'}>{row.state}</em>
        </motion.li>
    );
}

function PeopleCard() {
    return (
        <article className="landing-perf-card">
            <p className="landing-compare-label">Team performance</p>
            <ul className="landing-perf-list" data-testid="landing-compare-perf">
                {PERF.map((row) => {
                    const person = CAST[row.who];
                    return (
                        <li key={row.who} data-testid={`landing-perf-${row.who}`} className={row.score < 50 ? 'is-flag' : ''}>
                            <LandingFace who={row.who} size={32} radius={999} />
                            <span className="landing-perf-who">
                                <b>{person.name}</b>
                                <span>{row.done}/{row.assigned} done</span>
                            </span>
                            <AccountabilityScore
                                score={row.score}
                                label={row.label}
                                size="sm"
                                testId={`landing-perf-score-${row.who}`}
                            />
                        </li>
                    );
                })}
            </ul>
        </article>
    );
}

function TalkCard() {
    return (
        <div className="gmeet gmeet--film gmeet--talk" data-testid="landing-talk-frame" aria-label="Google Meet">
            <header className="gmeet-top">
                <span className="gmeet-title">1:1 · Follow-through</span>
                <LandingCastMark who="alex" size="sm" />
                <span className="gmeet-time">Fri 4:52 PM</span>
            </header>
            <div className="gmeet-grid gmeet-grid--two">
                <div className="gmeet-tile is-you">
                    <LandingFace who="alex" size={220} radius={0} />
                    <span className="gmeet-name"><Mic size={12} /> Alex (You)</span>
                </div>
                <div className="gmeet-tile is-speaking">
                    <LandingFace who="maya" size={220} radius={0} />
                    <span className="gmeet-name"><Mic size={12} /> Maya</span>
                </div>
            </div>
            <p className="gmeet-cc">{CAST.alex.name}: This is the third Friday.</p>
        </div>
    );
}

function FileCard({ progress }) {
    return (
        <article className="landing-hr-file" data-testid="landing-compare-file">
            <p className="landing-compare-label">If someone is a problem</p>
            <p className="landing-compare-file-lead">
                You start documenting. Record meetings. Pull Slack. Build a file.
            </p>
            <ul>
                {FILE_STEPS.map((step, i) => (
                    <FileStep key={step} step={step} index={i} progress={progress} />
                ))}
            </ul>
        </article>
    );
}

function FileStep({ step, index, progress }) {
    const at = 0.84 + index * 0.03;
    const opacity = useTransform(progress, [at, at + 0.05], [0.35, 1]);
    const x = useTransform(progress, [at, at + 0.05], [10, 0]);
    return (
        <motion.li style={{ opacity, x }}>{step}</motion.li>
    );
}

function FlowScene({ progress }) {
    const joinOp = useTransform(progress, [0, 0.04, 0.22, 0.32], [1, 1, 1, 0]);
    const tasksOp = useTransform(progress, [0.26, 0.36, 0.50, 0.60], [0, 1, 1, 0]);
    const appOp = useTransform(progress, [0.54, 0.64, 0.74, 0.82], [0, 1, 1, 0]);
    const chaseOp = useTransform(progress, [0.76, 0.84, 1], [0, 1, 1]);
    const peaceOp = useTransform(progress, [0.88, 0.96], [0, 1]);

    return (
        <div className="landing-flow-act" data-testid="landing-flow-frame">
            <motion.div className="landing-film-layer" style={{ opacity: joinOp }} data-testid="landing-flow-join">
                <JoinMeet progress={progress} />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: tasksOp }} data-testid="landing-flow-tasks">
                <TaskFlyout progress={progress} />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: appOp }}>
                <AppCard />
            </motion.div>
            <motion.div className="landing-film-layer" style={{ opacity: chaseOp }} data-testid="landing-flow-chase">
                <TskChase progress={progress} peaceOp={peaceOp} />
            </motion.div>
        </div>
    );
}

function JoinMeet({ progress }) {
    const botOp = useTransform(progress, [0.06, 0.16], [0, 1]);
    const botY = useTransform(progress, [0.06, 0.18], [24, 0]);
    const noteOp = useTransform(progress, [0.10, 0.18], [0, 1]);

    return (
        <div className="gmeet gmeet--film" data-testid="landing-flow-meet" aria-label="Google Meet">
            <header className="gmeet-top">
                <span className="gmeet-title">Q3 pipeline</span>
                <motion.span className="landing-bot-chip" style={{ opacity: noteOp }} data-testid="landing-flow-joined">
                    <TskFlowMark size={16} />
                    TskFlow joined
                </motion.span>
                <span className="gmeet-time">3:11 PM</span>
            </header>
            <div className="gmeet-grid">
                {MEET_PEOPLE.map((tile) => (
                    <div key={tile.who} className={`gmeet-tile${tile.you ? ' is-you' : ''}`}>
                        <LandingFace who={tile.who} size={220} radius={0} />
                        <span className="gmeet-name">
                            <Mic size={12} />
                            {CAST[tile.who].short}
                            {tile.you ? ' (You)' : ''}
                        </span>
                    </div>
                ))}
            </div>
            <motion.div className="landing-bot-tile" style={{ opacity: botOp, y: botY }} data-testid="landing-flow-bot">
                <TskFlowMark size={36} />
                <span>TskFlow is taking the tasks.</span>
            </motion.div>
        </div>
    );
}

function TaskFlyout({ progress }) {
    return (
        <div className="landing-flyout" data-testid="landing-flow-flyout">
            <p className="landing-flyout-kicker">Left the meet with</p>
            {TASKS.slice(0, 3).map((task, i) => (
                <FlyTask key={task.id} task={task} index={i} progress={progress} />
            ))}
        </div>
    );
}

function FlyTask({ task, index, progress }) {
    const at = 0.36 + index * 0.05;
    const opacity = useTransform(progress, [at, at + 0.06], [0, 1]);
    const y = useTransform(progress, [at, at + 0.07], [28 - index * 6, index * 8]);
    const rotate = useTransform(progress, [at, at + 0.07], [index % 2 === 0 ? -8 : 7, index % 2 === 0 ? -2 : 2]);
    return (
        <motion.article
            className={`landing-story-task landing-story-task--${task.tone} landing-fly-task`}
            style={{ opacity, y, rotate, zIndex: 4 - index }}
            data-testid={`landing-flow-task-${task.id}`}
        >
            <div className="landing-story-task-row">
                <LandingCastMark who={task.who} size="sm" />
                <span className="landing-story-task-title">{task.title}</span>
            </div>
            {task.due ? <span className="landing-clockchip landing-clockchip--sm">{task.due}</span> : null}
        </motion.article>
    );
}

function AppCard() {
    return <ProductCard />;
}

function ProductCard() {
    return (
        <article className="landing-app" data-testid="landing-app-card">
            <header className="landing-app-head">
                <span>Q3 forecast</span>
                <span className="landing-clockchip">Friday</span>
            </header>
            <div className="landing-app-pills">
                <span><Sparkles size={12} /> AI Summary</span>
                <span><Mail size={12} /> Email Assignee</span>
            </div>
            <div className="landing-app-tabs">
                <span className="is-on"><MessageSquare size={13} /> Chatter (3)</span>
                <span><Bell size={13} /> Reminders (2)</span>
            </div>
            <div className="landing-app-chat" data-testid="landing-app-chatter">
                <p>
                    <LandingFace who="maya" size={22} radius={999} />
                    <span>Yep, I&apos;ll have this done by Friday.</span>
                </p>
                <p>
                    <TskFlowMark size={18} />
                    <span>Captured from the meet. I&apos;ll follow up if this goes quiet.</span>
                </p>
            </div>
            <div className="landing-app-assigned" data-testid="landing-app-assigned">
                <Users size={16} />
                <span>Assigned to</span>
                <LandingFace who="maya" size={28} radius={999} />
                <b>{CAST.maya.name}</b>
            </div>
        </article>
    );
}

function TskChase({ progress, peaceOp }) {
    const mailOp = useTransform(progress, [0.78, 0.86], [0, 1]);
    const slackOp = useTransform(progress, [0.84, 0.92], [0, 1]);

    return (
        <div className="landing-flow-end">
            <motion.article className="gmail gmail--film" style={{ opacity: mailOp }} data-testid="landing-lived-gmail" aria-label="Gmail">
                <header className="gmail-top">
                    <span className="gmail-word">Gmail</span>
                    <span className="gmail-search">Search mail</span>
                </header>
                <article className="gmail-read">
                    <h3>Q3 forecast</h3>
                    <div className="gmail-tools" aria-hidden>
                        <Archive size={16} />
                        <Trash2 size={16} />
                        <Clock size={16} />
                    </div>
                    <div className="gmail-from">
                        <TskFlowMark size={28} />
                        <div>
                            <p><b>TskFlow</b> <span>&lt;tasks@tskflow.com&gt;</span></p>
                            <p>to {CAST.maya.short}</p>
                        </div>
                    </div>
                    <p className="gmail-text" data-testid="landing-gmail-body">
                        Still on the Q3 forecast for Friday.
                    </p>
                </article>
            </motion.article>
            <motion.article className="landing-slack landing-slack--film" style={{ opacity: slackOp }} data-testid="landing-lived-slack">
                <div className="landing-slack-head">
                    <TskFlowMark size={22} />
                    <span className="landing-slack-name">TskFlow</span>
                    <span className="landing-slack-time">Fri</span>
                </div>
                <p className="landing-slack-body">Maya — the Q3 forecast is still open.</p>
                <p className="sr-only"># q3-forecast</p>
            </motion.article>
            <motion.div className="landing-peace-strip" style={{ opacity: peaceOp }} data-testid="landing-flow-peace">
                {FLOW_STEPS.map((step) => (
                    <span key={step}>{step}</span>
                ))}
                <p data-testid="landing-compare-calm">
                    You can see who delivered without asking around.
                    <span className="sr-only">Leaders see who follows through. HR already has the record.</span>
                </p>
            </motion.div>
        </div>
    );
}
